import { Injectable, inject } from '@angular/core';
import { CloudLogsService, LogType } from 'src/app/shared/services/cloud-logs.service';
import { ILifeTest, IPolConf, POL_LIVE_FRAME_SOURCE_IDS } from '../../models/life-check';
import { DocsLog, OcrLog, PolLog, PolManualLog, TEST_STATUS } from '../../models/user-identification';

export interface IKoDebugImageEntry {
	image: string;
	sourceId: string;
	photoIndex: number;
}

export interface IKoDebugOcrDocument {
	documentTestId: string;
	photos?: Array<{ img?: string }>;
}

@Injectable({
	providedIn: 'root',
})
export class KoDebugImagesService {
	private cloudLogsService = inject(CloudLogsService);

	public shouldSendKoDebugLogs(valid: boolean, status: TEST_STATUS | string): boolean {
		return !valid && typeof status === 'string' && status !== TEST_STATUS.OK;
	}

	public collectOcrKoDebugEntries(
		documents: IKoDebugOcrDocument[],
		capturedImage?: string | null,
		currentFrame?: string,
	): IKoDebugImageEntry[] {
		const entries: IKoDebugImageEntry[] = [];

		documents.forEach((document) => {
			document.photos?.forEach((photo, photoIndex) => {
				if (photo?.img?.startsWith('data:image/')) {
					entries.push({
						image: photo.img,
						sourceId: document.documentTestId,
						photoIndex,
					});
				}
			});
		});

		this.appendUniqueKoDebugFrame(entries, capturedImage, 'captured');
		this.appendUniqueKoDebugFrame(entries, currentFrame, POL_LIVE_FRAME_SOURCE_IDS.FINAL);
		return entries;
	}

	public collectPolKoDebugEntries(
		lifeCheckConf: IPolConf,
		currentFrame?: string,
		currentFrameSourceId: string = POL_LIVE_FRAME_SOURCE_IDS.FINAL,
	): IKoDebugImageEntry[] {
		const entries: IKoDebugImageEntry[] = [];
		this.appendPolMiddleFacePhotos(entries, lifeCheckConf);
		lifeCheckConf.lifeTest.forEach((lifeTest) => {
			this.appendPolLifeTestPhotos(entries, lifeTest);
		});
		this.appendUniqueKoDebugFrame(entries, currentFrame, currentFrameSourceId);
		return entries;
	}

	public sendOcrKoDebugLogs(
		valid: boolean,
		status: TEST_STATUS | string,
		context: 'ocr' | 'manual-ocr',
		documents: IKoDebugOcrDocument[],
		capturedImage?: string | null,
		currentFrame?: string,
		extraData: Record<string, unknown> = {},
	): void {
		const entries = this.collectOcrKoDebugEntries(documents, capturedImage, currentFrame);
		this.sendKoDebugImageEntries(valid, status, context, entries, extraData);
	}

	/** KO final de operación PoL (p. ej. `ko-maxtries`, permisos). */
	public sendPolKoDebugLogs(
		valid: boolean,
		status: TEST_STATUS | string,
		lifeCheckConf: IPolConf,
		currentFrame?: string,
		extraData: Record<string, unknown> = {},
	): void {
		const reason = this.extractReason(extraData);
		const frameSourceId = this.resolvePolKoDebugFrameSourceId(
			lifeCheckConf,
			status,
			currentFrame,
			reason,
			POL_LIVE_FRAME_SOURCE_IDS.FINAL,
		);
		const entries = this.collectPolKoDebugEntries(lifeCheckConf, currentFrame, frameSourceId);
		this.sendKoDebugImageEntries(valid, status, 'pol', entries, extraData);
	}

	public sendManualPolKoDebugLogs(
		valid: boolean,
		status: TEST_STATUS | string,
		photoImg?: string | null,
		extraData: Record<string, unknown> = {},
	): void {
		const entries: IKoDebugImageEntry[] = [];
		this.appendUniqueKoDebugFrame(entries, photoImg, 'manual-pol');
		this.sendKoDebugImageEntries(valid, status, 'manual-pol', entries, extraData);
	}

	public sendDocsKoDebugLogs(
		valid: boolean,
		status: TEST_STATUS | string,
		context: string,
		candidates: Array<string | null | undefined>,
		extraData: Record<string, unknown> = {},
	): void {
		const entries: IKoDebugImageEntry[] = [];
		candidates.forEach((candidate, photoIndex) => {
			if (candidate?.startsWith('data:image/')) {
				entries.push({
					image: candidate,
					sourceId: context,
					photoIndex,
				});
			}
		});
		this.sendKoDebugImageEntries(valid, status, context, entries, extraData);
	}

	private sendKoDebugImageEntries(
		valid: boolean,
		status: TEST_STATUS | string,
		context: string,
		entries: IKoDebugImageEntry[],
		extraData: Record<string, unknown> = {},
	): void {
		if (!this.shouldSendKoDebugLogs(valid, status) || !entries.length) {
			return;
		}

		const logInfo = this.resolveScreenshotLogInfo(context);
		const uniqueEntries = this.dedupeKoDebugEntries(entries);

		uniqueEntries.forEach((entry, index) => {
			this.cloudLogsService.sendCloudLogs(
				{
					status,
					context,
					koDebug: true,
					imageIndex: index + 1,
					imagesCount: uniqueEntries.length,
					sourceId: entry.sourceId,
					photoIndex: entry.photoIndex,
					...extraData,
				},
				logInfo,
				LogType.Info,
				entry.image,
			);
		});
	}

	private resolvePolKoDebugFrameSourceId(
		lifeCheckConf: IPolConf,
		status: TEST_STATUS | string,
		frame: string | undefined,
		reason: string | undefined,
		defaultSourceId: string,
	): string {
		if (!frame?.startsWith('data:image/') || !this.hasNoPolDebugPhotos(lifeCheckConf)) {
			return defaultSourceId;
		}
		return this.resolvePolLiveFrameSourceId(status, reason);
	}

	private hasNoPolDebugPhotos(lifeCheckConf: IPolConf): boolean {
		const hasMiddleFacePhoto = lifeCheckConf.middleFace.photos.some((photo) =>
			photo?.img?.startsWith('data:image/'),
		);
		if (hasMiddleFacePhoto) {
			return false;
		}

		return !lifeCheckConf.lifeTest.some((lifeTest) =>
			lifeTest.photos.some((photo) => photo?.img?.startsWith('data:image/')),
		);
	}

	private resolvePolLiveFrameSourceId(status: TEST_STATUS | string, reason?: string): string {
		if (reason?.includes('multipleFaces') || status === TEST_STATUS.KO_MULTIPLE_FACES) {
			return POL_LIVE_FRAME_SOURCE_IDS.MULTIPLE_FACES;
		}
		if (reason) {
			return this.toPolLiveFrameSourceId(reason);
		}
		if (typeof status === 'string' && status.startsWith('ko-')) {
			return `${status}-frame`;
		}
		return POL_LIVE_FRAME_SOURCE_IDS.DEFAULT;
	}

	private extractReason(extraData: Record<string, unknown>): string | undefined {
		return typeof extraData['reason'] === 'string' ? extraData['reason'] : undefined;
	}

	private appendPolMiddleFacePhotos(entries: IKoDebugImageEntry[], lifeCheckConf: IPolConf): void {
		lifeCheckConf.middleFace.photos.forEach((photo, photoIndex) => {
			if (photo?.img?.startsWith('data:image/')) {
				entries.push({
					image: photo.img,
					sourceId: lifeCheckConf.middleFace.lifeTestId,
					photoIndex,
				});
			}
		});
	}

	private appendPolLifeTestPhotos(entries: IKoDebugImageEntry[], lifeTest: ILifeTest): void {
		lifeTest.photos.forEach((photo, photoIndex) => {
			if (photo?.img?.startsWith('data:image/')) {
				entries.push({
					image: photo.img,
					sourceId: lifeTest.lifeTestId,
					photoIndex,
				});
			}
		});
	}

	private dedupeKoDebugEntries(entries: IKoDebugImageEntry[]): IKoDebugImageEntry[] {
		const uniqueEntries: IKoDebugImageEntry[] = [];
		const visited = new Set<string>();

		for (const entry of entries) {
			if (visited.has(entry.image)) {
				continue;
			}
			visited.add(entry.image);
			uniqueEntries.push(entry);
		}

		return uniqueEntries;
	}

	private appendUniqueKoDebugFrame(
		entries: IKoDebugImageEntry[],
		frame: string | null | undefined,
		sourceId: string,
	): void {
		if (!frame?.startsWith('data:image/') || entries.some((entry) => entry.image === frame)) {
			return;
		}

		entries.push({
			image: frame,
			sourceId,
			photoIndex: 0,
		});
	}

	private toPolLiveFrameSourceId(reason: string): string {
		const slug = reason
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, '-')
			.replace(/^-|-$/g, '')
			.slice(0, 64);

		return slug || POL_LIVE_FRAME_SOURCE_IDS.DEFAULT;
	}

	private resolveScreenshotLogInfo(context: string): string {
		switch (context) {
			case 'ocr':
			case 'manual-ocr':
				return OcrLog.SCREENSHOT;
			case 'pol':
				return PolLog.SCREENSHOT;
			case 'manual-pol':
				return PolManualLog.SCREENSHOT;
			case 'documents-permission-ko':
				return DocsLog.SCREENSHOT;
			default:
				return OcrLog.SCREENSHOT;
		}
	}
}
